import React, { useState, useMemo, useCallback } from 'react';
import {
  Title2,
  Text,
  Button,
  Spinner,
  Dropdown,
  Option,
  Badge,
  Card,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle24Filled, DismissCircle24Filled, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { createTmdsClient } from '../../services/tmds';
import { useQuery } from '@tanstack/react-query';

type DriftResult = {
  status: 'in-sync' | 'drifted' | 'error';
  sections?: Array<{ name: string; drifted: boolean; diff?: string }>;
  message?: string;
};

const DRIFT_TO_RELOAD: Record<string, string> = {
  globalRateLimits: 'rateLimits',
  accountModelLimits: 'accountModelLimits',
};

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  controls: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
  resultCard: { padding: tokens.spacingVerticalM, marginTop: tokens.spacingVerticalM },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
  },
  diffPre: {
    background: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre',
  },
  statusRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalM },
  syncColor: { color: tokens.colorPaletteGreenForeground1 },
  driftColor: { color: tokens.colorPaletteRedForeground1 },
  errorColor: { color: tokens.colorPaletteRedForeground1 },
  mutedBlock: { color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS, display: 'block' },
  diffDetails: { marginLeft: 'auto', maxWidth: '60%' },
  diffSummary: { cursor: 'pointer', fontSize: tokens.fontSizeBase200 },
  fieldLabel: { display: 'block', marginBottom: tokens.spacingVerticalXS },
  dropdownMinWidth: { minWidth: '300px', '@media (max-width: 480px)': { minWidth: '100%' } },
});

export default function ConfigDriftPage() {
  const styles = useStyles();
  const { getToken } = useMsalAuth();
  const client = useMemo(() => createTmdsClient(getToken), [getToken]);

  const myAccessQuery = useQuery({
    queryKey: ['myaccess'],
    queryFn: () => client.getMyAccess<Array<{ apiPath?: string; url_suffix?: string; name?: string }>>(),
  });

  const apiPaths = useMemo(() => {
    if (!Array.isArray(myAccessQuery.data)) return [];
    return myAccessQuery.data
      .map((item) => item.apiPath ?? item.url_suffix ?? '')
      .filter(Boolean)
      .sort();
  }, [myAccessQuery.data]);

  const [selectedPath, setSelectedPath] = useState('');
  const [checking, setChecking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState<DriftResult | null>(null);

  const checkDrift = useCallback(async () => {
    if (!selectedPath) return;
    setChecking(true);
    setResult(null);
    try {
      const data = await client.getTestConfig<Record<string, unknown>>(selectedPath);
      // Parse response to determine drift status
      const sections: DriftResult['sections'] = [];
      let hasDrift = false;

      if (data && typeof data === 'object') {
        for (const [key, value] of Object.entries(data)) {
          const v = value as Record<string, unknown> | undefined;
          const drifted = v?.drifted === true || v?.status === 'drifted';
          if (drifted) hasDrift = true;
          sections.push({
            name: key,
            drifted,
            diff: v?.diff ? JSON.stringify(v.diff, null, 2) : undefined,
          });
        }
      }

      setResult({
        status: hasDrift ? 'drifted' : 'in-sync',
        sections,
      });
    } catch (e) {
      setResult({ status: 'error', message: (e as Error).message });
    } finally {
      setChecking(false);
    }
  }, [client, selectedPath]);

  const refreshCache = useCallback(async () => {
    if (!selectedPath || !result?.sections) return;
    setRefreshing(true);
    try {
      const driftedSections = result.sections.filter((s) => s.drifted);
      const reloadTokens = driftedSections
        .map((s) => DRIFT_TO_RELOAD[s.name] || s.name)
        .filter(Boolean);
      await client.postRefresh(selectedPath, reloadTokens);
      // Re-check after refresh
      await checkDrift();
    } catch (e) {
      setResult((prev) => prev ? { ...prev, message: `Refresh failed: ${(e as Error).message}` } : null);
    } finally {
      setRefreshing(false);
    }
  }, [client, selectedPath, result, checkDrift]);

  const driftedCount = result?.sections?.filter((s) => s.drifted).length ?? 0;

  return (
    <div className={styles.container}>
      <Title2>Config Drift Detection</Title2>
      <Text>Compare running APIM configuration against stored config to detect drift.</Text>

      <div className={styles.controls}>
        <div>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            API Path
          </Text>
          <Dropdown
            value={selectedPath || undefined}
            selectedOptions={selectedPath ? [selectedPath] : []}
            onOptionSelect={(_e, data) => {
              if (data.optionValue) setSelectedPath(data.optionValue);
            }}
            placeholder="Select an API path"
            className={styles.dropdownMinWidth}
          >
            {apiPaths.map((p) => (
              <Option key={p} value={p}>
                {p}
              </Option>
            ))}
          </Dropdown>
        </div>
        <Button
          appearance="primary"
          onClick={checkDrift}
          disabled={!selectedPath || checking}
          icon={checking ? <Spinner size="tiny" /> : undefined}
        >
          {checking ? 'Checking…' : 'Check Drift'}
        </Button>
        {driftedCount > 0 && (
          <Button
            onClick={refreshCache}
            disabled={refreshing}
            icon={refreshing ? <Spinner size="tiny" /> : <ArrowClockwise16Regular />}
          >
            {refreshing ? 'Refreshing…' : 'Refresh Cache'}
          </Button>
        )}
      </div>

      {result && (
        <Card className={styles.resultCard}>
          <div className={styles.statusRow}>
            {result.status === 'in-sync' ? (
              <>
                <CheckmarkCircle24Filled className={styles.syncColor} />
                <Text weight="semibold" className={styles.syncColor}>
                  In Sync
                </Text>
              </>
            ) : result.status === 'drifted' ? (
              <>
                <DismissCircle24Filled className={styles.driftColor} />
                <Text weight="semibold" className={styles.driftColor}>
                  Drifted ({driftedCount} section{driftedCount === 1 ? '' : 's'})
                </Text>
              </>
            ) : (
              <Text className={styles.errorColor}>Error: {result.message}</Text>
            )}
          </div>

          {result.sections?.map((s) => (
            <div key={s.name} className={styles.sectionRow}>
              <Badge
                appearance="filled"
                color={s.drifted ? 'danger' : 'success'}
                size="small"
              >
                {s.drifted ? 'Drifted' : 'OK'}
              </Badge>
              <Text>{s.name}</Text>
              {s.diff && (
                <details className={styles.diffDetails}>
                  <summary className={styles.diffSummary}>Show diff</summary>
                  <pre className={styles.diffPre}>{s.diff}</pre>
                </details>
              )}
            </div>
          ))}

          {result.message && result.status !== 'error' && (
            <Text size={200} className={styles.mutedBlock}>
              {result.message}
            </Text>
          )}
        </Card>
      )}
    </div>
  );
}
